// @ts-nocheck
import React, { useState, useRef, useCallback, useMemo } from "react";
import ProcessedScatterGraph from "../forecastGraphs/ProcessedScatterGraph";
import { VariationMenu } from "../forecastGraphs/helpers/GraphMobileControls";
import {
  formatProcessedDataGraph,
  separatePercentileChannels,
} from "../../util/utils";

const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const isDateTimeKey = (key) => {
  const parsed = new Date(key);
  return !isNaN(parsed.getTime());
};

const normalizeGraphJSON = (json) => {
  // Shape 3: multi-model with "models" key
  if (json.models && typeof json.models === "object") {
    const variations = [];
    const models = {};

    Object.keys(json.models).forEach((key) => {
      const model = json.models[key];
      const rawData = model.data || model;
      const formatted = formatProcessedDataGraph(rawData);
      const { percentiles, otherChannels } =
        separatePercentileChannels(formatted);

      variations.push({
        key,
        name: model.variation ?? model.displayName ?? key,
        label: model.variation ?? model.displayName ?? key,
      });
      models[key] = {
        percentiles,
        otherChannels,
        yUnit: model.yUnit || json.yUnit || "",
      };
    });

    return {
      name: json.name || "Uploaded Graph",
      yUnit: json.yUnit || "",
      variations,
      models,
    };
  }

  // Shape 2: root metadata with "data" key
  if (json.data && typeof json.data === "object") {
    const formatted = formatProcessedDataGraph(json.data);
    const { percentiles, otherChannels } =
      separatePercentileChannels(formatted);

    return {
      name: json.name || "Uploaded Graph",
      yUnit: json.yUnit || "",
      variations: [{ key: "default", name: json.name || "Default", label: json.name || "Default" }],
      models: {
        default: {
          percentiles,
          otherChannels,
          yUnit: json.yUnit || "",
        },
      },
    };
  }

  // Shape 1: flat datetime-keyed data at root
  const keys = Object.keys(json);
  const datetimeKeys = keys.filter(isDateTimeKey);
  if (datetimeKeys.length > 0) {
    const dataOnly = datetimeKeys.reduce((acc, key) => {
      acc[key] = json[key];
      return acc;
    }, {});
    const formatted = formatProcessedDataGraph(dataOnly);
    const { percentiles, otherChannels } =
      separatePercentileChannels(formatted);

    return {
      name: "Uploaded Graph",
      yUnit: "",
      variations: [{ key: "default", name: "Default", label: "Default" }],
      models: {
        default: {
          percentiles,
          otherChannels,
          yUnit: "",
        },
      },
    };
  }

  throw new Error(
    "Unrecognized JSON format. Expected datetime-keyed data, or an object with a 'data' or 'models' key."
  );
};

const UploadGraphContent = () => {
  const [fileName, setFileName] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [error, setError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = useCallback((file) => {
    if (!file) return;

    setError(null);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const target = e && e.target;
        if (!target || target.result == null) {
          throw new Error("Failed to read file contents.");
        }

        if (typeof target.result !== "string") {
          throw new Error("Unexpected file content format.");
        }

        const json = JSON.parse(target.result);
        const normalized = normalizeGraphJSON(json);

        if (
          normalized.variations.length === 0 ||
          Object.keys(normalized.models).length === 0
        ) {
          throw new Error("No data found in the uploaded file.");
        }

        setParsedData(normalized);
        setSelectedVariation(normalized.variations[0].key);
      } catch (err) {
        setError(err.message || "Failed to parse JSON file.");
        setParsedData(null);
        setSelectedVariation(null);
      }
    };
    reader.onerror = () => {
      setError("Failed to read file.");
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleInputChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      e.target.value = "";
    },
    [handleFile]
  );

  const currentModel = useMemo(() => {
    if (!parsedData || !selectedVariation) return null;
    return parsedData.models[selectedVariation] || null;
  }, [parsedData, selectedVariation]);

  const variationControl = useMemo(() => {
    if (!parsedData || parsedData.variations.length <= 1) return null;
    return (
      <VariationMenu
        buttonClassName="h-9 w-10 sm:h-8 sm:w-auto px-2 sm:px-2 py-1 text-sm sm:text-xs border rounded-md bg-white shadow-sm flex items-center gap-1 hover:bg-gray-50 justify-center"
        showLabel
        variationOptions={parsedData.variations}
        selectedVariationKey={selectedVariation}
        onSelectVariation={setSelectedVariation}
      />
    );
  }, [parsedData, selectedVariation]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Upload Zone */}
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={[
          "w-full shrink-0 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors",
          currentModel ? "py-4 px-4" : "py-8 px-4",
          isDragOver
            ? "border-blue-400 bg-blue-50"
            : fileName && !error
            ? "border-green-400 bg-green-50"
            : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100",
        ].join(" ")}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          onChange={handleInputChange}
          className="hidden"
        />

        {fileName && !error ? (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-green-500 mb-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <p className="text-sm text-gray-700 font-Inter font-medium">
              {fileName}
            </p>
            <p className="text-xs text-gray-500 font-Inter mt-0.5">
              Click or drag to upload a different file
            </p>
          </>
        ) : (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-gray-400 mb-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p className="text-sm text-gray-600 font-Inter">
              Drag & drop a JSON file here, or click to browse
            </p>
          </>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="shrink-0 bg-red-50 border border-red-200 rounded-md p-3 mt-3">
          <p className="text-sm text-red-700 font-Inter">{error}</p>
        </div>
      )}

      {/* Graph Section - fills remaining space */}
      {currentModel && (
        <div className="flex-1 min-h-0 mt-3 flex flex-col">
          <ProcessedScatterGraph
            percentiles={currentModel.percentiles}
            otherChannels={currentModel.otherChannels}
            yaxis={currentModel.yUnit || parsedData.yUnit}
            timeZone={localTimeZone}
            assetTimeZone={localTimeZone}
            title={parsedData.name}
            desktopExtraControls={variationControl}
            desktopLeftContent={parsedData.name}
            hideGraphControls={false}
          />
        </div>
      )}
    </div>
  );
};

export default UploadGraphContent;
